import { PlusOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import { Button, Form, Input, Space, Typography } from 'antd';
import React, { useEffect, useMemo } from 'react';
import {
  ASSET_CATEGORY,
  MAX_LENGTHS,
  MODULE_TYPES,
  STATUS_TYPES
} from '../../../../../common/constants';
import { formValidatorRules } from '../../../../../common/utils';
import SelectAsset from '../../../../assets/components/SelectAsset';
import { Select } from '../../../../videos/components/FormInputs';
import { FORM_TYPES } from '../../../context/EditPageProvider';
import {
  CREATE_PAGE_MODULE,
  UPDATE_PAGE_MODULE
} from '../../../graphql/Mutations';
import { GET_PREFERENCES } from '../../../graphql/Queries';
import { ModuleFields, Permissions, ShowFields } from './FormInputs';

const initialNotificationListValue = {
  key: '',
  title: '',
  description: '',
  imageId: null
};

const getInitialValues = () => ({
  title: '',
  description: '',
  status: STATUS_TYPES.PUBLISHED,
  permissions: [],
  settings: {
    title: true,
    description: true
  },
  config: {
    notificationPreferences: [initialNotificationListValue]
  }
});

const SETTINGS = [
  {
    name: 'title',
    label: 'Title',
    allowedTypes: [MODULE_TYPES.NOTIFICATION_PREFERENCES_LIST]
  },
  {
    name: 'description',
    label: 'Description',
    allowedTypes: [MODULE_TYPES.NOTIFICATION_PREFERENCES_LIST]
  }
];

const NotificationInputs = ({ name, remove }) => {
  return (
    <>
      <Form.Item
        label="Key"
        name={[name, 'key']}
        rules={[formValidatorRules?.maxLength(MAX_LENGTHS.KEY)]}
      >
        <Select
          placeholder="Select Key"
          searchable={false}
          query={GET_PREFERENCES}
          variablesSelector={() => ({
            filter: {
              type: 'NOTIFICATION'
            }
          })}
          dataSelector={(data) => {
            return (
              data?.preferencesAdmin?.preferences?.map(
                ({ name: preferenceName, key }) => ({
                  label: preferenceName,
                  value: key
                })
              ) ?? []
            );
          }}
        />
      </Form.Item>
      <Form.Item
        label="Title"
        name={[name, 'title']}
        rules={[formValidatorRules?.maxLength(MAX_LENGTHS?.TITLE)]}
      >
        <Input placeholder="Enter title" />
      </Form.Item>
      <Form.Item
        label="Description"
        name={[name, 'description']}
        rules={[formValidatorRules?.maxLength(MAX_LENGTHS?.DESCRIPTION)]}
      >
        <Input.TextArea rows={4} placeholder="Enter description" />
      </Form.Item>
      <Form.Item label="Image" name={[name, 'asset']}>
        <SelectAsset
          modalTitle="Select Image"
          categoryKey={ASSET_CATEGORY.IMAGE}
          btnText="Image"
          dataSelector={({ id, url }) => ({
            id,
            url
          })}
        />
      </Form.Item>
      <Button
        className="text-btn mb-12"
        type="text"
        size="small"
        onClick={() => remove(name)}
      >
        Remove Data
      </Button>
    </>
  );
};

const NotificationPreferenceListForm = ({
  form: { type: formType, moduleId, defaultValues, index: order },
  pageId,
  type,
  onCancel,
  onSuccess
}) => {
  const [form] = Form.useForm();
  const isEdit = formType === FORM_TYPES.EDIT;
  const initialValues = useMemo(() => getInitialValues(), []);

  const MODULE_KEYS = {
    [MODULE_TYPES.NOTIFICATION_PREFERENCES_LIST]:
      'notificationPreferencesListModule'
  };

  const CONFIG_TITLE = {
    [MODULE_TYPES.NOTIFICATION_PREFERENCES_LIST]:
      'Notification Preference List Configs'
  };

  useEffect(() => {
    if (moduleId && defaultValues && formType === FORM_TYPES.EDIT && form) {
      form.setFieldsValue({
        title: defaultValues?.title ?? '',
        description: defaultValues?.description ?? '',
        status: defaultValues?.status ?? STATUS_TYPES.DRAFT,
        permissions:
          defaultValues?.permissions?.map((value) => ({
            label: value,
            value
          })) ?? [],
        settings: {
          ...initialValues.settings,
          ...defaultValues?.moduleData?.settings
        },
        config: {
          notificationPreferences: defaultValues?.moduleData?.config?.notificationPreferences?.map(
            (item) => ({
              ...item,
              asset: item?.image
            })
          )
        }
      });
    }
  }, [moduleId, formType, defaultValues, form, initialValues]);

  const [addEditModule, { loading }] = useMutation(
    isEdit ? UPDATE_PAGE_MODULE : CREATE_PAGE_MODULE
  );

  const handleSubmit = ({ config, settings, permissions, ...restValues }) => {
    const key = MODULE_KEYS[type];
    const notificationPreferencesConfig = config?.notificationPreferences?.map(
      (pref) => ({
        key: pref?.key?.value ?? pref?.key,
        title: pref?.title,
        description: pref?.description,
        imageId: pref?.asset?.id || null
      })
    );
    const payload = {
      [key]: {
        settings,
        config: {
          notificationPreferences: notificationPreferencesConfig
        }
      },
      ...(!isEdit && {
        type,
        order: order + 1
      }),
      permissions: permissions?.map(({ value }) => value),
      status: restValues?.status ?? null,
      title: restValues?.title ?? null,
      description: restValues?.description ?? null
    };
    addEditModule({
      variables: { data: payload, id: isEdit ? moduleId : pageId }
    }).then(
      ({
        data: {
          addUpdatedPageModule: { pageModule }
        }
      }) => {
        onSuccess(pageModule);
      }
    );
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={initialValues}
    >
      <ModuleFields isTrialSetup />
      <Space className="w-full" direction="vertical">
        <ShowFields settings={SETTINGS} type={type} />
      </Space>
      <Space className="w-full" direction="vertical">
        <Typography.Text>{CONFIG_TITLE[type]}</Typography.Text>
        <div>
          <Form.List name={['config', 'notificationPreferences']}>
            {(fields, { add, remove }) => (
              <>
                {fields?.map((field) => (
                  <NotificationInputs
                    form={form}
                    remove={remove}
                    key={field?.key}
                    {...field}
                  />
                ))}
                <Form.Item>
                  <Button
                    className="text-btn"
                    type="text"
                    onClick={() => add(initialNotificationListValue)}
                    icon={<PlusOutlined />}
                  >
                    Add Data
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        </div>
      </Space>
      <Permissions />
      <div className="d-flex button-section">
        <Space>
          <Form.Item>
            <Button
              disabled={loading}
              loading={loading}
              htmlType="submit"
              className="text-btn mr-8"
              type="text"
            >
              Save
            </Button>
          </Form.Item>
          <Form.Item>
            <Button
              type="text"
              disabled={loading}
              className="text-btn2"
              onClick={onCancel}
            >
              Cancel
            </Button>
          </Form.Item>
        </Space>
      </div>
    </Form>
  );
};

export default NotificationPreferenceListForm;
